import { API_ENDPOINTS, authFetch } from '../config/api';
import type {
  AudienceResponse,
  CreateCampaignInput,
  CreateCampaignResult,
  DeviceStats,
  NotifyUserInput,
  NotifyUserResult,
  PushAudience,
} from '../types/push';

/** Chama a API de notificações e devolve o corpo, ou lança o erro em português que ela mandou. */
const callApi = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const response = await authFetch(url, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(data.error || `Erro ${response.status} na API de notificações`);
  }
  return data as T;
};

export const fetchAudience = (audience: PushAudience): Promise<AudienceResponse> =>
  callApi<AudienceResponse>(API_ENDPOINTS.notifications.audience, {
    method: 'POST',
    body: JSON.stringify({ audience }),
  });

export const fetchDeviceStats = async (): Promise<DeviceStats> => {
  const data = await callApi<{ stats: DeviceStats }>(API_ENDPOINTS.notifications.stats);
  return data.stats;
};

export const createCampaign = (input: CreateCampaignInput): Promise<CreateCampaignResult> =>
  callApi<CreateCampaignResult>(API_ENDPOINTS.notifications.createCampaign, {
    method: 'POST',
    body: JSON.stringify(input),
  });

/** Push avulso para um usuário (ex.: resposta do suporte). Não vira campanha. */
export const notifyUser = (input: NotifyUserInput): Promise<NotifyUserResult> =>
  callApi<NotifyUserResult>(API_ENDPOINTS.notifications.notifyUser, {
    method: 'POST',
    body: JSON.stringify(input),
  });

/** Cancela uma campanha agendada (só funciona enquanto ela não foi disparada). */
export const cancelCampaign = async (campaignId: string): Promise<void> => {
  await callApi<{ success: true }>(API_ENDPOINTS.notifications.cancelCampaign, {
    method: 'POST',
    body: JSON.stringify({ campaignId }),
  });
};

/** Lê o arquivo como base64 (sem o prefixo "data:...;base64,"). */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem'));
    reader.readAsDataURL(file);
  });

/**
 * Sobe a imagem da notificação pela API (que grava no Storage com as
 * credenciais do servidor) e devolve o link. As regras do Storage não liberam
 * o navegador a gravar em notifications/.
 */
export const uploadNotificationImage = async (file: File): Promise<string> => {
  const data = await callApi<{ url: string }>(API_ENDPOINTS.notifications.uploadImage, {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, contentType: file.type, dataBase64: await fileToBase64(file) }),
  });
  return data.url;
};

/** Link da logo usada quando a notificação não tem imagem (FIAMImages no Storage). */
export const fetchDefaultLogo = async (): Promise<string | null> => {
  const data = await callApi<{ url: string | null }>(API_ENDPOINTS.notifications.defaultLogo);
  return data.url;
};
